import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { questionAPI } from '../api';
import api from '../api/axios';
import { useAuthStore } from '../store';
import Layout from '../components/layout/Layout';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'weak' | 'memo'>('overview');
  const [examTypeFilter, setExamTypeFilter] = useState<'전체' | '필기' | '실기'>('전체');
  const [memo, setMemo] = useState('');
  const [memoSaved, setMemoSaved] = useState(false);

  // ✅ 시험 기록 삭제
  const deleteResult = async (id: number) => {
    if (!window.confirm('이 기록을 삭제하시겠습니까?')) return;
    try {
      await questionAPI.myMockResults({ delete_id: id });
      setResults(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert('삭제 실패');
    }
  };

  // ✅ 메모 로드 - axios로 백엔드 직접 호출
  useEffect(() => {
    const loadMemo = async () => {
      try {
        const res = await api.get('/api/questions/memo/');
        if (res.data.memo !== undefined) {
          setMemo(res.data.memo);
        } else {
          const saved = localStorage.getItem('mohabwe_memo');
          if (saved) setMemo(saved);
        }
      } catch (e) {
        const saved = localStorage.getItem('mohabwe_memo');
        if (saved) setMemo(saved);
      }
    };
    loadMemo();
  }, []);

  // ✅ 메모 저장 - axios로 백엔드 직접 호출
  const saveMemo = async () => {
    try {
      await api.post('/api/questions/memo/', { memo });
      localStorage.setItem('mohabwe_memo', memo);
      setMemoSaved(true);
      setTimeout(() => setMemoSaved(false), 2000);
    } catch (e) {
      alert('저장 실패');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, resultsRes] = await Promise.all([
          questionAPI.myStats(),
          questionAPI.myMockResults(),
        ]);
        setStats(statsRes.data);
        setResults(resultsRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ fontSize: 16, color: 'var(--text-muted)' }}>로딩 중...</div>
      </div>
    </Layout>
  );

  // 점수 추이 데이터 (필터 적용)
  const trendData = (stats?.score_trend || [])
    .filter((t: any) => examTypeFilter === '전체' || t.exam_type === examTypeFilter);

  // 취약 과목 바 차트용 (상위 8개)
  const radarData = (stats?.weak_subjects || [])
    .slice(0, 8)
    .map((s: any) => ({
      subject: s.subject.length > 5 ? s.subject.slice(0, 5) + '..' : s.subject,
      정답률: s.rate,
    }));

  // 과목별 바 차트 데이터
  const barData = (stats?.weak_subjects || []).map((s: any) => ({
    name: s.subject.length > 6 ? s.subject.slice(0, 6) + '..' : s.subject,
    정답률: s.rate,
    오답률: 100 - s.rate,
  }));

  // 필터된 결과 목록
  const filteredResults = results.filter(r =>
    examTypeFilter === '전체' || r.exam_type === examTypeFilter
  );

  const MOCK_TYPE_COLORS: Record<string, string> = {
    '일반': '#3498db', 'D': '#e74c3c', 'R': '#2ecc71', 'S': '#9b59b6'
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>

        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>
              👤 {user?.username}님의 마이페이지
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              학습 현황과 취약점을 확인하세요
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← 뒤로</button>
        </div>

        {/* 요약 카드 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: '총 시험 횟수', value: `${stats?.total_exams || 0}회`, icon: '📝', color: '#3498db' },
            { label: '필기 평균', value: `${stats?.pilgi_avg || 0}점`, icon: '📚', color: '#2ecc71' },
            { label: '실기 평균', value: `${stats?.silgi_avg || 0}점`, icon: '🔧', color: '#e67e22' },
            { label: '필기 오답', value: `${stats?.wrong_counts?.pilgi || 0}개`, icon: '❌', color: '#e74c3c' },
            { label: '실기 오답', value: `${stats?.wrong_counts?.silgi || 0}개`, icon: '❌', color: '#9b59b6' },
          ].map((card, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderTop: `3px solid ${card.color}` }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{card.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{card.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{card.label}</div>
            </div>
          ))}
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
          {([
            { key: 'overview', label: '📊 점수 추이' },
            { key: 'weak', label: '⚠️ 취약점 분석' },
            { key: 'history', label: '📋 시험 기록' },
            { key: 'memo', label: '📝 메모장' },
          ] as const).map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '8px 16px', fontSize: 13, fontWeight: 700, border: 'none',
                background: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)',
                marginBottom: -2,
              }}>
              {tab.label}
            </button>
          ))}

          {/* 필터 */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {(['전체', '필기', '실기'] as const).map(t => (
              <button key={t}
                onClick={() => setExamTypeFilter(t)}
                className={`btn btn-sm ${examTypeFilter === t ? 'btn-primary' : 'btn-ghost'}`}
                style={{ fontSize: 12 }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 점수 추이 탭 */}
        {activeTab === 'overview' && (
          <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>📈 최근 점수 추이</div>
            {trendData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                아직 시험 기록이 없습니다.<br />모의고사를 풀고 결과를 저장해보세요!
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis domain={[0, 100]} fontSize={11} unit="점" />
                  <Tooltip
                    formatter={(value: any, name: any, props: any) => [
                      `${value}점`,
                      `${props.payload.exam_type} ${props.payload.mock_type}모의고사`
                    ]}
                  />
                  <Line type="monotone" dataKey="score" stroke="#3498db" strokeWidth={2}
                    dot={{ fill: '#3498db', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}

        {/* 취약점 분석 탭 */}
        {activeTab === 'weak' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* 과목별 정답률 바 차트 */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>📊 과목별 정답률</div>
              {radarData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>데이터가 없습니다</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={radarData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} unit="%" fontSize={11} />
                    <YAxis type="category" dataKey="subject" fontSize={11} width={60} />
                    <Tooltip formatter={(v: any) => `${v}%`} />
                    <Bar dataKey="정답률" fill="#3498db" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* 과목별 바 차트 */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>📊 과목별 정답/오답률</div>
              {barData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>데이터가 없습니다</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" fontSize={10} />
                    <YAxis unit="%" fontSize={10} />
                    <Tooltip formatter={(v: any) => `${v}%`} />
                    <Legend fontSize={11} />
                    <Bar dataKey="정답률" fill="#2ecc71" radius={[4,4,0,0]} />
                    <Bar dataKey="오답률" fill="#e74c3c" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* 취약 과목 목록 */}
            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>⚠️ 취약 과목 순위 (정답률 낮은 순)</div>
              {(stats?.weak_subjects || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>데이터가 없습니다</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(stats?.weak_subjects || []).map((s: any, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, width: 24, color: i < 3 ? '#e74c3c' : 'var(--text-muted)' }}>
                        {i + 1}위
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, width: 120 }}>{s.subject}</span>
                      <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                        <div style={{
                          width: `${s.rate}%`, height: '100%', borderRadius: 4,
                          background: s.rate >= 70 ? '#2ecc71' : s.rate >= 50 ? '#f39c12' : '#e74c3c',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, width: 50, textAlign: 'right',
                        color: s.rate >= 70 ? '#2ecc71' : s.rate >= 50 ? '#f39c12' : '#e74c3c' }}>
                        {s.rate}%
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 70 }}>
                        {s.correct}/{s.total}문제
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 시험 기록 탭 */}
        {activeTab === 'history' && (
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {filteredResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>시험 기록이 없습니다</div>
            ) : (
              <>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>
                  총 {filteredResults.length}회 시험 기록
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left' }}>날짜</th>
                      <th style={{ padding: '10px 16px', textAlign: 'left' }}>종목</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>구분</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>유형</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>점수</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>정답/총문제</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>합격여부</th>
                      <th style={{ padding: '10px 16px', textAlign: 'center' }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map((r, i) => {
                      const isPassed = r.exam_type === '필기' ? r.score >= 60 : r.score >= 60;
                      return (
                        <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                          <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{r.created_at}</td>
                          <td style={{ padding: '10px 16px', fontWeight: 600 }}>{r.subject}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                              background: r.exam_type === '필기' ? '#dbeafe' : '#fef3c7',
                              color: r.exam_type === '필기' ? '#2563eb' : '#d97706' }}>
                              {r.exam_type}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                              background: MOCK_TYPE_COLORS[r.mock_type] + '22',
                              color: MOCK_TYPE_COLORS[r.mock_type] }}>
                              {r.mock_type}모의고사
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 15, fontWeight: 800,
                            color: isPassed ? '#2ecc71' : '#e74c3c' }}>
                            {r.score}점
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)' }}>
                            {r.correct_count}/{r.total_count}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: isPassed ? '#2ecc71' : '#e74c3c' }}>
                              {isPassed ? '🎉 합격' : '📚 불합격'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <button onClick={() => deleteResult(r.id)}
                              className="btn btn-danger btn-sm" style={{ fontSize: 11 }}>
                              삭제
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
        {/* 메모장 탭 */}
        {activeTab === 'memo' && (
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>📝 나의 메모장</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {memoSaved && <span style={{ fontSize: 12, color: '#2ecc71', fontWeight: 700 }}>✅ 저장됨</span>}
                <button className="btn btn-primary btn-sm" onClick={saveMemo}>💾 저장</button>
              </div>
            </div>
            <div style={{ padding: 4 }}>
              <textarea
                value={memo}
                onChange={e => setMemo(e.target.value)}
                placeholder="문제풀기 화면의 메모장에서 작성한 내용이 여기에 표시됩니다.&#10;여기서 수정 후 저장하면 문제풀기 화면에도 반영됩니다."
                style={{
                  width: '100%', minHeight: 500, border: 'none', outline: 'none',
                  resize: 'vertical', padding: '16px', fontSize: 14,
                  lineHeight: 1.8, fontFamily: 'inherit', boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyPage;
